import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { chaveDoEscopo } from '../catalog';
import type {
  ConsultaDaBiblioteca,
  PaginaDeRelatorios,
  ReportJob,
  ReportOperation,
  ReportPreferences,
  ReportsScope,
} from '../types';
import {
  cancelReport,
  createReport,
  interpretReport,
  listReports,
  loadReports,
  openReport,
  pollReport,
  savePreferences,
} from './actions';

export interface ReportsState {
  /** `workspaceId:produto` do que está na tela; resposta de outro escopo é descartada. */
  scope: string | null;
  jobs: ReportJob[];
  /** `null` até o load: a tela usa o padrão do catálogo do produto. */
  preferences: ReportPreferences | null;
  selectedId: string | null;
  openRequestId: string | null;
  loading: boolean;
  creating: boolean;
  planning: boolean;
  /**
   * Um erro por operação: o diálogo repete a que falhou, e só o de `poll` pausa o
   * acompanhamento (A16). O download fica no hook dele, porque o PDF é efeito do navegador.
   */
  errors: Partial<Record<ReportOperation, string>>;
  /** O que a última gravação de preferências tentava salvar, para repetir igual. */
  failedPreferences: ReportPreferences | null;
  /**
   * Quantas gravações de preferência terminaram no escopo, e quantas havia quando o load começou:
   * a leitura do load é mais velha que uma gravação concluída no meio dele e não a sobrescreve.
   */
  preferencesRevision: number;
  loadPreferencesRevision: number;
  requestId: string | null;
  requests: string[];
  /** Paginação da consulta atual da biblioteca; o cursor só vale para `query`. */
  library: {
    query: ConsultaDaBiblioteca;
    nextCursor: string | null;
    hasMore: boolean;
    loadingMore: boolean;
    requestId: string | null;
    /** Contadores do core; o que a tela cria ou conclui depois soma aqui até a próxima página. */
    counts: { all: number; ready: number } | null;
  };
}
const initialLibrary: ReportsState['library'] = {
  query: {},
  nextCursor: null,
  hasMore: false,
  loadingMore: false,
  requestId: null,
  counts: null,
};
const MENSAGENS: Record<ReportOperation, string> = {
  load: 'Não foi possível carregar seus relatórios. Tente novamente.',
  loadMore: 'Não foi possível carregar mais relatórios. Tente novamente.',
  poll: 'A atualização foi interrompida. Seu relatório continua salvo; tente reconectar.',
  open: 'Não foi possível abrir este relatório. Ele pode não estar disponível neste espaço.',
  interpret:
    'Não consegui entender seu pedido agora. Seu texto continua aqui para tentar novamente.',
  create: 'Não foi possível concluir esta ação. Tente novamente.',
  cancel: 'Não foi possível concluir esta ação. Tente novamente.',
  preferences: 'Não foi possível salvar suas preferências.',
};
/** Recusas cuja mensagem já diz o que fazer (`causaDaRecusa` do gateway): valem mais que a genérica. */
const CAUSAS_COM_MENSAGEM = new Set(['SEM_ACESSO', 'MUITOS_PEDIDOS', 'RELATORIOS_LEX_DESLIGADO']);
const mensagemDaFalha = (
  operacao: ReportOperation,
  erro: { code?: string; message?: string },
): string =>
  erro.code && erro.message && CAUSAS_COM_MENSAGEM.has(erro.code)
    ? erro.message
    : MENSAGENS[operacao];
const contadoresDaPagina = (pagina: PaginaDeRelatorios): ReportsState['library']['counts'] =>
  pagina.counts ? { all: pagina.counts.all, ready: pagina.counts.ready } : null;
const novoPrimeiro = (a: ReportJob, b: ReportJob) =>
  b.createdAt.localeCompare(a.createdAt) || b.id.localeCompare(a.id);
/** Une a página aos já conhecidos sem regredir versão, na ordem do keyset do core. */
function mesclar(jobs: ReportJob[], pagina: ReportJob[]) {
  for (const job of pagina) {
    const index = jobs.findIndex((item) => item.id === job.id);
    if (index < 0) jobs.push(job);
    else if (jobs[index].version <= job.version) jobs[index] = job;
  }
  jobs.sort(novoPrimeiro);
}
/** A resposta só vale se for de uma requisição ainda viva do escopo que está na tela. */
const daRequisicaoViva = (state: ReportsState, scope: ReportsScope, requestId: string) =>
  state.scope === chaveDoEscopo(scope) && state.requests.includes(requestId);
function encerrar(state: ReportsState, requestId: string) {
  state.requests = state.requests.filter((id) => id !== requestId);
}
const initialState: ReportsState = {
  scope: null,
  jobs: [],
  preferences: null,
  selectedId: null,
  openRequestId: null,
  loading: false,
  creating: false,
  planning: false,
  errors: {},
  failedPreferences: null,
  preferencesRevision: 0,
  loadPreferencesRevision: 0,
  requestId: null,
  requests: [],
  library: initialLibrary,
};
const slice = createSlice({
  name: 'relatoriosLex',
  initialState,
  reducers: {
    resetReports: () => initialState,
    selectReport: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
      state.openRequestId = null;
      state.errors = state.errors.poll ? { poll: state.errors.poll } : {};
    },
    /**
     * Falha declarada fora de um thunk: o acompanhamento que desistiu no teto do backoff (`poll`)
     * ou o link do aviso que não abriu (`open`, que não pausa o acompanhamento do ativo).
     */
    operationFailed: (
      state,
      action: PayloadAction<{ scope: string; operation: 'poll' | 'open' }>,
    ) => {
      if (state.scope === action.payload.scope)
        state.errors[action.payload.operation] = MENSAGENS[action.payload.operation];
    },
    reportErrorCleared: (state, action: PayloadAction<ReportOperation>) => {
      delete state.errors[action.payload];
    },
  },
  extraReducers(builder) {
    builder.addCase(interpretReport.pending, (state, action) => {
      if (state.scope === chaveDoEscopo(action.meta.arg.scope)) {
        state.planning = true;
        delete state.errors.interpret;
        state.requests.push(action.meta.requestId);
      }
    });
    builder.addCase(interpretReport.fulfilled, (state, action) => {
      if (!daRequisicaoViva(state, action.meta.arg.scope, action.meta.requestId)) return;
      state.planning = false;
      encerrar(state, action.meta.requestId);
    });
    builder.addCase(interpretReport.rejected, (state, action) => {
      if (!daRequisicaoViva(state, action.meta.arg.scope, action.meta.requestId)) return;
      state.planning = false;
      encerrar(state, action.meta.requestId);
      state.errors.interpret = mensagemDaFalha('interpret', action.error);
    });
    builder.addCase(loadReports.pending, (state, action) => {
      const scope = chaveDoEscopo(action.meta.arg);
      if (state.scope !== scope) Object.assign(state, initialState);
      state.scope = scope;
      state.loading = true;
      state.errors = {};
      state.requestId = action.meta.requestId;
      state.loadPreferencesRevision = state.preferencesRevision;
    });
    builder.addCase(loadReports.fulfilled, (state, action) => {
      if (
        state.scope !== chaveDoEscopo(action.meta.arg) ||
        state.requestId !== action.meta.requestId
      )
        return;
      // Mescla em vez de trocar: um poll mais novo que chegou durante o load não regride.
      mesclar(
        state.jobs,
        action.payload.page.items.filter((job) => job.workspaceId === action.meta.arg.workspaceId),
      );
      if (state.preferencesRevision === state.loadPreferencesRevision)
        state.preferences = action.payload.preferences ?? state.preferences;
      state.library = {
        ...initialLibrary,
        nextCursor: action.payload.page.nextCursor,
        hasMore: action.payload.page.hasMore,
        counts: contadoresDaPagina(action.payload.page),
      };
      state.loading = false;
    });
    builder.addCase(loadReports.rejected, (state, action) => {
      if (state.requestId !== action.meta.requestId) return;
      state.loading = false;
      state.errors.load = mensagemDaFalha('load', action.error);
    });
    builder.addCase(listReports.pending, (state, action) => {
      if (state.scope !== chaveDoEscopo(action.meta.arg.scope)) return;
      state.library.requestId = action.meta.requestId;
      state.library.loadingMore = !!action.meta.arg.cursor;
      delete state.errors[action.meta.arg.cursor ? 'loadMore' : 'load'];
    });
    builder.addCase(listReports.fulfilled, (state, action) => {
      if (
        state.scope !== chaveDoEscopo(action.meta.arg.scope) ||
        state.library.requestId !== action.meta.requestId
      )
        return;
      mesclar(
        state.jobs,
        action.payload.items.filter((job) => job.workspaceId === action.meta.arg.scope.workspaceId),
      );
      state.library = {
        query: action.meta.arg.query,
        nextCursor: action.payload.nextCursor,
        hasMore: action.payload.hasMore,
        loadingMore: false,
        requestId: null,
        counts: contadoresDaPagina(action.payload),
      };
    });
    builder.addCase(listReports.rejected, (state, action) => {
      if (
        state.scope !== chaveDoEscopo(action.meta.arg.scope) ||
        state.library.requestId !== action.meta.requestId
      )
        return;
      state.library.loadingMore = false;
      state.library.requestId = null;
      // A busca que falhou se refaz pelo load: ele volta a consulta a vazio e o container busca de novo.
      const operacao = action.meta.arg.cursor ? 'loadMore' : 'load';
      state.errors[operacao] = mensagemDaFalha(operacao, action.error);
    });
    builder.addCase(createReport.pending, (state, action) => {
      if (state.scope === chaveDoEscopo(action.meta.arg.scope)) {
        state.creating = true;
        delete state.errors.create;
        state.requests.push(action.meta.requestId);
      }
    });
    for (const [thunk, operacao] of [
      [createReport, 'create'],
      [pollReport, 'poll'],
      [openReport, 'open'],
      [cancelReport, 'cancel'],
    ] as const) {
      if (thunk !== createReport)
        builder.addCase(thunk.pending, (state, action) => {
          if (state.scope !== chaveDoEscopo(action.meta.arg.scope)) return;
          state.requests.push(action.meta.requestId);
          if (operacao === 'cancel') delete state.errors.cancel;
          if (operacao === 'open') {
            state.openRequestId = action.meta.requestId;
            state.selectedId = null;
            delete state.errors.open;
          }
        });
      builder.addCase(thunk.fulfilled, (state, action) => {
        if (!daRequisicaoViva(state, action.meta.arg.scope, action.meta.requestId)) return;
        encerrar(state, action.meta.requestId);
        if (operacao === 'open' && state.openRequestId !== action.meta.requestId) return;
        if (operacao === 'open') state.openRequestId = null;
        if (action.type === createReport.fulfilled.type) state.creating = false;
        // Resposta de outro escritório ou de outro relatório é descartada como falha, sem travar a tela.
        if (
          action.payload.workspaceId !== action.meta.arg.scope.workspaceId ||
          ('id' in action.meta.arg && action.payload.id !== action.meta.arg.id)
        ) {
          if (operacao !== 'poll') state.errors[operacao] = MENSAGENS[operacao];
          return;
        }
        const index = state.jobs.findIndex((job) => job.id === action.payload.id);
        const contadores = state.library.counts;
        if (index < 0) {
          mesclar(state.jobs, [action.payload]);
          // Só o relatório criado agora é novo no total; o aberto por link já está no total do core.
          if (contadores && action.type === createReport.fulfilled.type) contadores.all += 1;
        } else if (state.jobs[index].version <= action.payload.version) {
          if (
            contadores &&
            state.jobs[index].status !== 'completed' &&
            action.payload.status === 'completed'
          )
            contadores.ready += 1;
          state.jobs[index] = action.payload;
        }
        if (action.type === createReport.fulfilled.type || operacao === 'open')
          state.selectedId = action.payload.id;
        delete state.errors[operacao];
      });
      builder.addCase(thunk.rejected, (state, action) => {
        if (!daRequisicaoViva(state, action.meta.arg.scope, action.meta.requestId)) return;
        encerrar(state, action.meta.requestId);
        if (operacao === 'open' && state.openRequestId !== action.meta.requestId) return;
        if (operacao === 'open') state.openRequestId = null;
        if (action.type === createReport.rejected.type) state.creating = false;
        // Falha isolada de poll não é erro: quem decide pausar é o acompanhamento, no teto do backoff.
        if (operacao === 'poll') return;
        state.errors[operacao] =
          typeof action.payload === 'string'
            ? action.payload
            : mensagemDaFalha(operacao, action.error);
      });
    }
    builder.addCase(savePreferences.pending, (state, action) => {
      if (state.scope !== chaveDoEscopo(action.meta.arg.scope)) return;
      state.requests.push(action.meta.requestId);
      delete state.errors.preferences;
      state.failedPreferences = null;
    });
    builder.addCase(savePreferences.fulfilled, (state, action) => {
      if (!daRequisicaoViva(state, action.meta.arg.scope, action.meta.requestId)) return;
      encerrar(state, action.meta.requestId);
      state.preferencesRevision += 1;
      state.preferences = action.payload;
    });
    builder.addCase(savePreferences.rejected, (state, action) => {
      if (!daRequisicaoViva(state, action.meta.arg.scope, action.meta.requestId)) return;
      encerrar(state, action.meta.requestId);
      state.errors.preferences = mensagemDaFalha('preferences', action.error);
      state.failedPreferences = action.meta.arg.preferences;
    });
  },
});
export const { operationFailed, reportErrorCleared, resetReports, selectReport } = slice.actions;
export const relatoriosLexReducer = slice.reducer;
